import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Card, SectionTitle, Spinner, TextArea, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';

interface Settings {
  name: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  baseFare: string;
  perKm: string;
  perMinute: string;
  minimumFare: string;
  airportSurcharge: string;
  nightSurchargePercent: number;
  quoteValidityMinutes: number;
  recurringHorizonDays: number;
  cancellationFee: string;
  commissionEnabled: boolean;
  commissionPercent: string;
  commissionGraceDays: number;
  commissionBlocking: boolean;
  freeCancelMinutes: number;
  noShowWaitMinutes: number;
  freeWaitMinutes: number;
  airportFreeWaitMinutes: number;
  waitFeePerMinute: string;
  rideCheckStopMinutes: number;
  blockOnExpiredDocuments: boolean;
  blockAcceptance: boolean;
  blockStart: boolean;
  requireVerifiedDocuments: boolean;
  documentReminderDays: number[];
  gpsIntervalSeconds: number;
  endLocationWarningMeters: number;
  chatRetentionDays: number;
  hideSensitivePushContent: boolean;
  coverageText: string | null;
  cancellationPolicy: string | null;
  bankAccounts: Array<{ bank: string; accountType: string; number: string; holder: string; holderId?: string }>;
  landing: { heroTitle: string; heroSubtitle: string; faqs: Array<{ q: string; a: string }>; benefits: Array<{ title: string; text: string }> };
}

type FormValues = Omit<Settings, 'documentReminderDays'> & { documentReminderDays: string };

const num = (v: unknown) => (v === '' || v === undefined ? undefined : Number(v));

export function SettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api<Settings>('/settings') });
  const form = useForm<FormValues>();
  const banks = useFieldArray({ control: form.control, name: 'bankAccounts' });
  const faqs = useFieldArray({ control: form.control, name: 'landing.faqs' });

  useEffect(() => {
    if (q.data) form.reset({ ...q.data, documentReminderDays: q.data.documentReminderDays.join(', ') });
  }, [q.data, form]);

  const save = form.handleSubmit(async (v) => {
    try {
      const body = {
        name: v.name,
        email: v.email || undefined,
        phone: v.phone || undefined,
        whatsapp: v.whatsapp || undefined,
        baseFare: v.baseFare,
        perKm: v.perKm,
        perMinute: v.perMinute,
        minimumFare: v.minimumFare,
        airportSurcharge: v.airportSurcharge,
        nightSurchargePercent: num(v.nightSurchargePercent),
        quoteValidityMinutes: num(v.quoteValidityMinutes),
        recurringHorizonDays: num(v.recurringHorizonDays),
        cancellationFee: v.cancellationFee || '0',
        commissionEnabled: v.commissionEnabled,
        commissionPercent: v.commissionPercent || '5',
        commissionGraceDays: num(v.commissionGraceDays),
        commissionBlocking: v.commissionBlocking,
        freeCancelMinutes: num(v.freeCancelMinutes),
        noShowWaitMinutes: num(v.noShowWaitMinutes),
        freeWaitMinutes: num(v.freeWaitMinutes),
        airportFreeWaitMinutes: num(v.airportFreeWaitMinutes),
        waitFeePerMinute: v.waitFeePerMinute || '0',
        rideCheckStopMinutes: num(v.rideCheckStopMinutes),
        blockOnExpiredDocuments: v.blockOnExpiredDocuments,
        blockAcceptance: v.blockAcceptance,
        blockStart: v.blockStart,
        requireVerifiedDocuments: v.requireVerifiedDocuments,
        documentReminderDays: v.documentReminderDays
          .split(',')
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isInteger(n) && n >= 0),
        gpsIntervalSeconds: num(v.gpsIntervalSeconds),
        endLocationWarningMeters: num(v.endLocationWarningMeters),
        chatRetentionDays: num(v.chatRetentionDays),
        hideSensitivePushContent: v.hideSensitivePushContent,
        coverageText: v.coverageText ?? undefined,
        cancellationPolicy: v.cancellationPolicy ?? undefined,
        bankAccounts: v.bankAccounts,
        landing: { heroTitle: v.landing.heroTitle, heroSubtitle: v.landing.heroSubtitle, faqs: v.landing.faqs, benefits: v.landing.benefits },
      };
      await api('/settings', { method: 'PATCH', body });
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await qc.invalidateQueries({ queryKey: ['public-profile'] });
      toast('Configuración guardada', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  });

  if (q.isLoading) return <Page title="Configuración" back="/driver/more"><Spinner /></Page>;
  const r = form.register;
  const check = 'tap flex items-center gap-3 text-sm';

  return (
    <Page title="Configuración" back="/driver/more">
      <form onSubmit={save} className="flex flex-col gap-6">
        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Negocio" />
          <TextField label="Nombre comercial" {...r('name')} />
          <div className="grid sm:grid-cols-3 gap-3">
            <TextField label="Teléfono" type="tel" {...r('phone')} />
            <TextField label="WhatsApp" type="tel" hint="+1809…" {...r('whatsapp')} />
            <TextField label="Correo" type="email" {...r('email')} />
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Tarifa sugerida" subtitle="Base + km × tarifa por km + minutos × tarifa por minuto + peajes + suplementos. El precio final siempre lo decides tú." />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <TextField label="Tarifa base (RD$)" inputMode="decimal" {...r('baseFare')} />
            <TextField label="Por km (RD$)" inputMode="decimal" {...r('perKm')} />
            <TextField label="Por minuto (RD$)" inputMode="decimal" {...r('perMinute')} />
            <TextField label="Tarifa mínima (RD$)" inputMode="decimal" {...r('minimumFare')} />
            <TextField label="Suplemento aeropuerto (RD$)" inputMode="decimal" {...r('airportSurcharge')} />
            <TextField label="Recargo nocturno (%)" type="number" {...r('nightSurchargePercent')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Validez de cotización (min)" type="number" {...r('quoteValidityMinutes')} />
            <TextField label="Horizonte de recurrencias (días)" type="number" {...r('recurringHorizonDays')} />
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Comisión semanal" subtitle="Cada lunes se cobra a cada conductor este % de lo que cobró por viajes la semana anterior (sin propinas). Transfieren a las cuentas bancarias de abajo y tú confirmas en Más → Comisiones. Los administradores no pagan." />
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('commissionEnabled')} /> Cobrar comisión semanal</label>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Porcentaje (%)" inputMode="decimal" {...r('commissionPercent')} />
            <TextField label="Días para pagar" type="number" min={0} max={14} {...r('commissionGraceDays')} />
          </div>
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('commissionBlocking')} /> Pausar al conductor que no pague a tiempo</label>
          <p className="text-xs text-[var(--text-muted)]">Pausado: se desconecta y no puede cotizar ni aceptar viajes nuevos hasta que reporte el pago (sus viajes ya confirmados sí los hace). Si la transferencia no llegó, en Comisiones toca "No llegó" y vuelve a quedar pausado.</p>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Cancelaciones y seguridad" subtitle="Cargo si el cliente cancela con el conductor ya en camino (pasados los minutos gratis) o no se presenta. 0 = sin cargo." />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Cargo por cancelación (RD$)" inputMode="decimal" {...r('cancellationFee')} />
            <TextField label="Minutos para cancelar gratis" type="number" {...r('freeCancelMinutes')} />
            <TextField label="Espera antes de 'no se presentó' (min)" type="number" {...r('noShowWaitMinutes')} />
            <TextField label="'¿Todo bien?' tras detenido (min)" type="number" {...r('rideCheckStopMinutes')} />
            <TextField label="Espera gratis (min)" type="number" {...r('freeWaitMinutes')} />
            <TextField label="Espera gratis en aeropuerto (min)" type="number" {...r('airportFreeWaitMinutes')} />
            <TextField label="Cargo por minuto de espera (RD$, 0 = no cobrar)" inputMode="decimal" {...r('waitFeePerMinute')} />
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-3">
          <SectionTitle title="Documentos" />
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('requireVerifiedDocuments')} /> Los socios necesitan sus documentos obligatorios subidos y verificados por ti para cotizar e iniciar viajes</label>
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('blockOnExpiredDocuments')} /> Bloquear operaciones si un documento obligatorio está vencido</label>
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('blockAcceptance')} /> …al aceptar o cotizar nuevos servicios</label>
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('blockStart')} /> …al iniciar servicios</label>
          <TextField label="Avisos antes del vencimiento (días)" hint="Separados por coma, ej. 30, 15, 7, 1, 0" {...r('documentReminderDays')} />
        </Card>

        <Card className="p-5 flex flex-col gap-3">
          <SectionTitle title="Viajes y privacidad" />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Intervalo GPS (s)" type="number" {...r('gpsIntervalSeconds')} />
            <TextField label="Aviso de llegada lejos del destino (m)" type="number" {...r('endLocationWarningMeters')} />
            <TextField label="Retención del chat (días)" type="number" {...r('chatRetentionDays')} />
          </div>
          <label className={check}><input type="checkbox" className="size-5 accent-[#E5252A]" {...r('hideSensitivePushContent')} /> Ocultar detalles en notificaciones de pantalla bloqueada</label>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Cuentas para transferencias" subtitle="Se muestran al cliente al momento de pagar." action={
            <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => banks.append({ bank: '', accountType: 'Ahorros', number: '', holder: '' })}>Agregar</Button>
          } />
          {banks.fields.map((f, i) => (
            <div key={f.id} className="grid grid-cols-2 gap-3 rounded-2xl border border-[var(--border)] p-3">
              <TextField label="Banco" {...r(`bankAccounts.${i}.bank`)} />
              <TextField label="Tipo" {...r(`bankAccounts.${i}.accountType`)} />
              <TextField label="Número" {...r(`bankAccounts.${i}.number`)} />
              <TextField label="Titular" {...r(`bankAccounts.${i}.holder`)} />
              <TextField label="Cédula/RNC (opcional)" {...r(`bankAccounts.${i}.holderId`)} />
              <div className="flex items-end">
                <Button variant="danger" size="sm" icon={<Trash2 className="size-4" />} onClick={() => banks.remove(i)}>Quitar</Button>
              </div>
            </div>
          ))}
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <SectionTitle title="Página pública" />
          <TextField label="Título principal" {...r('landing.heroTitle')} />
          <TextArea label="Subtítulo" {...r('landing.heroSubtitle')} />
          <TextArea label="Cobertura" {...r('coverageText')} />
          <TextArea label="Política de cancelación" {...r('cancellationPolicy')} />
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Preguntas frecuentes</h3>
            <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => faqs.append({ q: '', a: '' })}>Agregar</Button>
          </div>
          {faqs.fields.map((f, i) => (
            <div key={f.id} className="flex flex-col gap-2 rounded-2xl border border-[var(--border)] p-3">
              <TextField label="Pregunta" {...r(`landing.faqs.${i}.q`)} />
              <TextArea label="Respuesta" {...r(`landing.faqs.${i}.a`)} />
              <Button variant="danger" size="sm" className="self-end" icon={<Trash2 className="size-4" />} onClick={() => faqs.remove(i)}>Quitar</Button>
            </div>
          ))}
        </Card>

        <Button type="submit" size="lg" loading={form.formState.isSubmitting}>Guardar configuración</Button>
      </form>
    </Page>
  );
}
